import { Avatar, Button, Form, notification, Popover, Select, Space, Tooltip, Typography } from "antd";
import { SlDocs } from "react-icons/sl";
import { EmailThreadData } from "dtos/email.dto";
import {
  TbPaperclip,
  TbChevronDown,
  TbDownload,
  TbCopy,
  TbEyeOff,
  TbSend,
  TbSparkles,
  TbLoader2,
  TbFileSymlink,
  TbFileTypePdf,
  TbFileTypeTxt,
  TbFileTypeZip,
  TbFileTypeDoc,
  TbFileTypeXls,
  TbFileTypePpt,
  TbFileMusic,
  TbVideo,
  TbBinary,
  TbFileUnknown,
  TbPhoto,
} from "react-icons/tb";
import {
  formatBytes,
  formatDateTime,
  formatMoney,
  getFirstAlphabet,
  parseEmailAddress,
  parseEmailTitle,
} from "utils/functions";
import { IconType } from "react-icons/lib";
import { hashString } from "utils/colors";
import { useLayoutEffect, useRef, useState } from "react";
import { EmailBody } from "./email-body";
import { MdOutlineKeyboardDoubleArrowRight } from "react-icons/md";
import { ApiService } from "api/api.service";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import GenerateOfferModal from "./GenerateOfferModal";
import OfferAssistant from "./OfferAssistant/OfferAssistant";

export const getIcons = (type: string) => {
  const map: Record<string, IconType> = {
    image: TbPhoto,
    video: TbVideo,
    audio: TbFileMusic,
    pdf: TbFileTypePdf,
    text: TbFileTypeTxt,
    archive: TbFileTypeZip,
    spreadsheet: TbFileTypeXls,
    document: TbFileTypeDoc,
    presentation: TbFileTypePpt,
    binary: TbBinary,
  };

  const Icon = map[type] || TbFileUnknown;
  return <Icon />;
};

export const getClassName = (type: string) => {
  const CONTENT_TYPE_TW_CLASS_MAP: Record<string, string> = {
    image: "bg-green-100 text-green-600 border-green-600/40 border-2",
    video: "bg-purple-100 text-purple-600 border-purple-600/40 border-2",
    audio: "bg-blue-100 text-blue-600 border-blue-600/40 border-2",
    pdf: "bg-lime-100 text-lime-700 border-lime-700/40 border-2",
    text: "bg-gray-100 text-gray-700 border-gray-700/40 border-2",
    archive: "bg-yellow-100 text-yellow-700 border-yellow-700/40 border-2",
    spreadsheet: "bg-red-100 text-red-600 border-red-600/40 border-2",
    document: "bg-indigo-100 text-indigo-700 border-indigo-700/40 border-2",
    presentation: "bg-pink-100 text-pink-700 border-pink-700/40 border-2",
    binary: "bg-slate-100 text-slate-700 border-slate-700/40 border-2",
    unknown: "bg-neutral-100 text-neutral-500 border-neutral-500/40 border-2",
  };

  return CONTENT_TYPE_TW_CLASS_MAP[type] || CONTENT_TYPE_TW_CLASS_MAP["unknown"];
};

export const colors = [
  { bg: "#ede9fe", text: "#7c3aed" }, // violet
  { bg: "#e0e7ff", text: "#6366f1" }, // indigo
  { bg: "#dbeafe", text: "#2563eb" }, // blue
  { bg: "#cffafe", text: "#06b6d4" }, // cyan
  { bg: "#ccfbf1", text: "#14b8a6" }, // teal
  { bg: "#d1fae5", text: "#10b981" }, // emerald
  { bg: "#dcfce7", text: "#22c55e" }, // green
  { bg: "#ecfccb", text: "#84cc16" }, // lime
  { bg: "#fef3c7", text: "#eab308" }, // yellow
  { bg: "#ffedd5", text: "#f97316" }, // orange
  { bg: "#fee2e2", text: "#ef4444" }, // red
  { bg: "#fce7f3", text: "#ec4899" }, // pink
  { bg: "#f5d0fe", text: "#c026d3" }, // fuchsia
];

type RecipientVariant = "to" | "cc" | "bcc";

const RECIPIENT_STYLES: Record<RecipientVariant, { label: string; icon: IconType; pill: string; badge: string }> = {
  to: {
    label: "To",
    icon: TbSend,
    pill: "bg-indigo-50 border-indigo-200 text-indigo-900",
    badge: "bg-indigo-100 text-indigo-700 hover:bg-indigo-200",
  },
  cc: {
    label: "Cc",
    icon: TbCopy,
    pill: "bg-sky-50 border-sky-200 text-sky-900",
    badge: "bg-sky-100 text-sky-700 hover:bg-sky-200",
  },
  bcc: {
    label: "Bcc",
    icon: TbEyeOff,
    pill: "bg-slate-100 border-slate-300 text-slate-800",
    badge: "bg-slate-200 text-slate-700 hover:bg-slate-300",
  },
};

const ITEM_GAP = 6;
const PILL_CHROME = 30;

function useFittedCount(items: string[]) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const ghostRef = useRef<HTMLDivElement>(null);
  const [count, setCount] = useState(items.length);

  const key = items.join("\u0000");

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const ghost = ghostRef.current;
    if (!wrap || !ghost) return;

    const measure = () => {
      const nodes = Array.from(ghost.querySelectorAll<HTMLElement>("[data-m='item']"));
      const widths = nodes.map((n) => n.getBoundingClientRect().width);
      if (!widths.length) return;

      const labelWidth = labelRef.current?.getBoundingClientRect().width ?? 0;
      const available = wrap.clientWidth - labelWidth - PILL_CHROME;
      const total = widths.reduce((sum, w) => sum + w + ITEM_GAP, -ITEM_GAP);

      if (total <= available) {
        setCount(widths.length);
        return;
      }

      const reserve =
        (ghost.querySelector<HTMLElement>("[data-m='badge']")?.getBoundingClientRect().width ?? 0) + ITEM_GAP;

      let used = 0;
      let fitted = 0;
      for (const w of widths) {
        const next = used + w + (fitted ? ITEM_GAP : 0);
        if (next + reserve > available) break;
        used = next;
        fitted += 1;
      }
      setCount(Math.max(1, fitted));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(wrap);
    return () => observer.disconnect();
  }, [key]);

  return { wrapRef, labelRef, ghostRef, count };
}

function RecipientPill({
  variant,
  values,
  highlight,
}: Readonly<{ variant: RecipientVariant; values?: string[]; highlight?: boolean }>) {
  const items = values?.filter(Boolean) ?? [];
  const { wrapRef, labelRef, ghostRef, count } = useFittedCount(items);

  const { label, icon: Icon, pill, badge } = RECIPIENT_STYLES[variant];
  const visible = items.slice(0, count);
  const hidden = items.slice(count);
  const single = visible.length === 1;

  return (
    <div ref={wrapRef} className="w-fit min-w-0" hidden={!items.length}>
      <div
        className={`inline-flex max-w-full items-center gap-2 overflow-hidden rounded-full border border-solid px-2 py-0.5 ${pill} ${highlight ? "bg-white/70" : ""}`}
      >
        <span
          ref={labelRef}
          className="flex flex-shrink-0 items-center gap-1 text-xs font-semibold lowercase font-mono opacity-50"
        >
          <Icon size={13} />
          {label}
        </span>

        <div className="relative flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
          {visible.map((email) => (
            <Tooltip key={email} title={single ? email : undefined}>
              <span className={`text-xs leading-5 ${single ? "min-w-0 truncate" : "flex-shrink-0 whitespace-nowrap"}`}>
                {parseEmailAddress(email)}
              </span>
            </Tooltip>
          ))}

          {hidden.length > 0 && (
            <Popover
              trigger={["hover", "click"]}
              placement="bottom"
              content={
                <div className="flex max-h-48 max-w-xs flex-col gap-1 overflow-y-auto">
                  {hidden.map((email) => (
                    <Typography.Text key={email} style={{ margin: 0 }} className="break-all text-xs text-gray-700">
                      {email}
                    </Typography.Text>
                  ))}
                </div>
              }
            >
              <span
                className={`flex-shrink-0 cursor-pointer rounded-full px-1.5 text-xs font-medium leading-5 transition-colors ${badge} ${highlight ? "bg-slate-100 hover:bg-slate-200" : ""}`}
              >
                +{hidden.length}
              </span>
            </Popover>
          )}

          <div
            ref={ghostRef}
            aria-hidden
            className="pointer-events-none invisible absolute left-0 top-0 flex items-center gap-1.5 whitespace-nowrap"
          >
            {items.map((email) => (
              <span key={email} data-m="item" className="text-xs leading-5">
                {email}
              </span>
            ))}
            <span data-m="badge" className="rounded-full px-1.5 text-xs font-medium leading-5">
              +{items.length}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

interface EmailMessageProps {
  message: EmailThreadData;
  onReply: (messageId: string) => void;
  onCancel: () => void;
  messageRef: (el: HTMLDivElement | null) => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
  showSubject: boolean;
  outgoing: boolean;
  className?: string;
  highlight?: boolean;
  topBorder?: boolean | "full";
  bottomBorder?: boolean | "full";
  toggleNegotiationPanel: () => void;
  isPanelOpen: boolean;
  isInboxPanelOpen: boolean;
  openInboxPanel: () => void;
  updateNegotiationContext: (context: {
    application_id?: string;
    loan_id?: string;
    thread_id?: string;
    message_id?: string;
  }) => void;
  closeInboxPanel: () => void;
}

export default function EmailMessage({
  message,
  onReply,
  onCancel,
  messageRef,
  closeInboxPanel,
  isExpanded,
  onToggleExpand,
  showSubject,
  outgoing,
  className,
  highlight,
  topBorder = false,
  bottomBorder = false,
  isPanelOpen,
  toggleNegotiationPanel,
  openInboxPanel,
  isInboxPanelOpen,
  updateNegotiationContext,
}: Readonly<EmailMessageProps>) {
  // const { app_key } = useParams<{ app_key: string }>();
  const { orgCode } = useParams<{ orgCode: string }>();
  const firstChar = getFirstAlphabet(parseEmailTitle(message.from).toUpperCase()) || "U";
  const colorIndex = hashString(parseEmailAddress(message.from)) % colors.length;
  const { text, bg } = colors[colorIndex];
  const qc = useQueryClient();
  const firstOutbound = ApiService.negotiation.useFirstOutbound();
  const followUp = ApiService.negotiation.useFollowup();
  const [attachLoanOpen, setAttachLoanOpen] = useState(false);
  const [selectedLoanContext, setSelectedLoanContext] = useState<string | undefined>(undefined);
  const [isofferGeneratorPanelOpen, setIsofferGeneratorPanelOpen] = useState(false);
  const fullApplication = ApiService.application.useGetByApplicationId(message.application);
  const loanDetails = fullApplication.data?.data.application.loans ?? [];
  const attachLoanToThread = ApiService.email.useAttachLoanToThread();
  const savedOffersQuery = ApiService.negotiation.useSavedCustomOffers({
    application_id: message.thread.application,
    loan_id: message.thread.loanId,
    thread_id: message?.thread?.externalId,
    message_id: message?.thread?.lastMessage as unknown as string,
  });
  const savedOffers = savedOffersQuery.data?.result ?? [];

  const attachLoanContent = (
    <div style={{ width: 320 }}>
      <Form.Item layout="vertical" label="Select Loan" name="loan">
        <Select
          labelInValue
          style={{ width: "100%" }}
          placeholder="Select a loan"
          onChange={({ label, value }) => {
            setSelectedLoanContext(value);
          }}
          options={loanDetails.map((loan) => ({
            label: loan.creditor_name,
            value: loan._id,
            fundedAmount: loan.funded_amount,
            payments: loan.payments,
            frequency: loan.frequency,
          }))}
          labelRender={(option) => {
            const loan = loanDetails.find((l) => l._id === option.value);
            if (!loan?.funded_amount) return option.label;
            return `${loan.creditor_name} (${formatMoney(loan.funded_amount)})`;
          }}
          optionRender={(option) => (
            <div>
              <Typography.Text>{option.data.label}</Typography.Text>
              {option.data.fundedAmount ? (
                <div className="text-xs opacity-80">
                  {formatMoney(option.data.fundedAmount)} funded
                  {option.data.payments ? (
                    <>
                      {"; "}
                      {formatMoney(option.data.payments)} {option.data.frequency}
                    </>
                  ) : null}
                </div>
              ) : null}
            </div>
          )}
        />
      </Form.Item>

      <div className="flex justify-end gap-2 mt-3">
        <Button size="small" onClick={() => setAttachLoanOpen(false)}>
          Cancel
        </Button>
        <Button
          size="small"
          type="primary"
          loading={firstOutbound.isPending}
          disabled={!selectedLoanContext}
          onClick={() => {
            attachLoanToThread.mutate(
              { threadId: message.thread.externalId, loanId: selectedLoanContext as string },
              {
                onSuccess: () => {
                  notification.success({ message: "Loan added to thraed" });
                  setAttachLoanOpen(false);
                  if (orgCode) {
                    qc.invalidateQueries({ queryKey: ["get-thread-messages", message.thread.externalId, orgCode] });
                  } else {
                    qc.invalidateQueries({ queryKey: ["get-thread-messages", message.thread.externalId] });
                  }
                },
              },
            );
          }}
        >
          Update
        </Button>
      </div>
    </div>
  );

  const handleFollowUp = () => {
    followUp.mutate(
      {
        application_id: message.thread.application,
        loan_id: message.thread.loanId,

        thread_id: message.thread.externalId,
        message_id: message.thread.lastMessage as unknown as string,
        orgCode: orgCode,
      },
      {
        onSuccess: () => {
          updateNegotiationContext({
            application_id: message.thread.application,
            loan_id: message.thread.loanId as string,
            thread_id: message.thread.externalId,
          });
          toggleNegotiationPanel();
          closeInboxPanel();
          qc.invalidateQueries({ queryKey: ["negotiationv2", "smart-panel", "get-smart-panel"] });
        },
      },
    );
  };

  const collapsedView = (
    <div
      ref={messageRef}
      onClick={onToggleExpand}
      className={`flex items-center pb-4 justify-between cursor-pointer transition-colors`}
    >
      <div className="flex gap-3 items-center flex-1 min-w-0">
        <Avatar
          style={{
            backgroundColor: text,
            color: bg,
            aspectRatio: 1,
          }}
          className={`uppercase font-semibold`}
        >
          {firstChar}
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Typography.Text className="font-semibold text-sm text-gray-900 truncate">
              {parseEmailTitle(message.from)}
              &nbsp;<span className="opacity-60 font-normal">&lt;{parseEmailAddress(message.from)}&gt;</span>
            </Typography.Text>
          </div>
          <Typography.Text className="text-xs text-gray-600 truncate block">
            {message.text || "No preview available"}
          </Typography.Text>
        </div>
        <Typography.Text style={{ margin: 0 }} className="text-xs text-gray-500 flex-shrink-0">
          {formatDateTime(message.deliveredAt)}
        </Typography.Text>
      </div>
      {message.attachments.length > 0 && (
        <div className="flex items-center gap-1 ml-2 flex-shrink-0">
          <TbPaperclip size={16} className="text-gray-400" />
          <Typography.Text style={{ margin: 0 }} className="text-xs text-gray-500">
            {message.attachments.length}
          </Typography.Text>
        </div>
      )}
    </div>
  );

  const expandedView = (
    <div ref={messageRef} className="pb-4">
      <div className="flex items-start justify-between">
        <div className="flex flex-col items-start">
          <div className="flex gap-3 items-center">
            <Avatar
              style={{
                backgroundColor: text,
                color: bg,
                aspectRatio: 1,
              }}
              className={`uppercase font-semibold`}
            >
              {firstChar}
            </Avatar>

            <div>
              <Typography.Paragraph style={{ margin: 0 }} className="font-semibold">
                {parseEmailTitle(message.from) || parseEmailAddress(message.from).split("@")[0]}
              </Typography.Paragraph>
              <Typography.Paragraph style={{ margin: 0 }} className="font-normal opacity-75">
                {parseEmailAddress(message.from)}
              </Typography.Paragraph>
            </div>
          </div>
          <div className="flex flex-col gap-1 min-w-0 pt-1">
            <div className="flex min-w-0 flex-wrap items-start gap-1.5 pt-1 pb-2">
              <RecipientPill variant="to" values={message.to} highlight={highlight} />
              <RecipientPill variant="cc" values={message.cc} highlight={highlight} />
              {outgoing && <RecipientPill variant="bcc" values={message.bcc} highlight={highlight} />}
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end gap-2">
          <Typography.Text style={{ margin: 0 }} className="text-xs text-gray-500">
            {formatDateTime(message.deliveredAt)}
          </Typography.Text>
          <Space>
            {highlight ? (
              <Button
                variant="solid"
                size="small"
                onClick={() => onCancel()}
                className="text-gray-500 hover:text-gray-700 text-xs"
              >
                Cancel
              </Button>
            ) : (
              <Button
                variant="solid"
                color="primary"
                size="small"
                className="text-white/80 hover:text-white text-xs"
                onClick={() => onReply(message._id)}
              >
                Reply
              </Button>
            )}
            <Tooltip title="Collapse">
              <Button
                type="text"
                size="small"
                onClick={onToggleExpand}
                icon={<TbChevronDown size={20} />}
                className="text-xs text-gray-500 relative top-0.5 hover:text-gray-700 h-auto p-1"
              />
            </Tooltip>
          </Space>
        </div>
      </div>

      <EmailBody title={message.subject} html={message.html} text={message.text} />

      {message.attachments.length > 0 && (
        <div className="mt-3 space-y-2">
          <Typography.Paragraph style={{ margin: 0 }} className="text-sm font-semibold text-gray-500">
            {message.attachments.length} Attachment
          </Typography.Paragraph>

          {message.attachments.map((att, i) => (
            <div
              key={i}
              className="flex items-center justify-between bg-gray-50 p-3 rounded-lg 
                   border border-gray-300"
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-8 h-8 rounded flex items-center justify-center text-xs font-bold 
              ${getClassName(att?.type || "unknown")}
            `}
                >
                  {getIcons(att?.type || "unknown")}
                </div>

                <div>
                  <Typography.Paragraph style={{ margin: 0 }} className="text-xs font-medium text-gray-900">
                    {att?.name}
                  </Typography.Paragraph>
                  <Typography.Paragraph style={{ margin: 0 }} className="text-xs text-gray-500">
                    {formatBytes(att?.size ? Number(att.size) : 0)}
                  </Typography.Paragraph>
                </div>
              </div>

              <a download={att?.name} href={att?.url} target="_blank" rel="noreferrer">
                <Button type="text" className="hover:bg-gray-200 rounded px-2 h-auto">
                  <TbDownload className="text-gray-600" />
                </Button>
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div
      className={`space-y-3 px-12 border-0 border-gray-200 border-solid last:border-b-0 ${highlight ? "bg-indigo-50 border-l-indigo-200 border-l-4 sticky top-0 bottom-0 z-10" : ""} ${topBorder === "full" && "border-t"} ${bottomBorder === "full" && "border-b"} transition-colors ${className}`}
    >
      <div className={`flex items-center justify-between ${showSubject && "py-3"}`}>
        <div className="flex items-center gap-1">
          {message && !isInboxPanelOpen && showSubject && (
            <Tooltip title="Open inbox">
              <Button
                variant="text"
                type="text"
                onClick={openInboxPanel}
                icon={<MdOutlineKeyboardDoubleArrowRight size={20} />}
              />
            </Tooltip>
          )}
          <Typography.Title style={{ margin: 0 }} level={4} hidden={!showSubject}>
            {message.subject}
          </Typography.Title>
        </div>
        <div className="flex items-center gap-2">
          {showSubject &&
            message.thread.application &&
            message.thread.loanId &&
            (isPanelOpen ? null : (
              <Button
                variant="solid"
                color="magenta"
                icon={<TbSparkles />}
                onClick={() => {
                  updateNegotiationContext({
                    application_id: message.thread.application,
                    loan_id: message.thread.loanId as string,
                    thread_id: message.thread.externalId,
                  });
                  toggleNegotiationPanel();
                  closeInboxPanel();
                }}
                style={{ background: "transparent", overflow: "hidden", isolation: "isolate" }}
              >
                <div className="mesh_bg" />
                Suggestion
              </Button>
            ))}
          {showSubject && message.thread.application && !message.thread.loanId && (
            <Tooltip title="Attach loan">
              <Popover
                content={attachLoanContent}
                trigger="click"
                open={attachLoanOpen}
                onOpenChange={setAttachLoanOpen}
                placement="bottomRight"
              >
                <Button loading={firstOutbound.isPending} icon={<TbFileSymlink />}>
                  Attach Loan
                </Button>
              </Popover>
            </Tooltip>
          )}
        </div>
        {/* {!showSubject && message.application && message.loanId && !isPanelOpen && (
          <Button
            loading={followUp.isPending}
            onClick={handleFollowUp}
            className=""
            icon={followUp.isPending ? <TbLoader2 className="animate-spin" /> : <TbSparkles />}
            type="primary"
          >
            Follow Up
          </Button>
         
        )} */}
      </div>
      <div
        className={`border-0 ${topBorder === true && "border-t"} ${bottomBorder === true && "border-b"} border-solid border-gray-200`}
      >
        {isExpanded ? expandedView : collapsedView}
      </div>
      {/* <GenerateOfferModal
        application_id={message.thread.application}
        loanId={message.thread.loanId as string}
        onClose={() => setIsofferGeneratorPanelOpen(false)}
        open={isofferGeneratorPanelOpen}
        thread_id={message?.thread?.externalId}
        message_id={message?.thread?.lastMessage as unknown as string}
      /> */}
      {/* <OfferAssistant
        isOpen={isPanelOpen}
        onClose={() => togg(false)}
        negotiationContext={{
          application_id: message.thread.application,
          loan_id: message.thread.loanId,
          thread_id: message.thread.externalId,
        }}
        clientName=""
      /> */}
    </div>
  );
}
